import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { Buildings, X } from "@phosphor-icons/react";
import { useNavCounts } from "@/contexts/NavCountsContext";

/**
 * Create or edit a Pending Task.
 *
 * Modes:
 *  - Single (default): firm picker visible, POST /api/tasks
 *  - Bulk: pass `bulkFirms = [{subdomain, firm_name}, ...]` (length >= 1).
 *    Firm picker is hidden, a "N firms selected" chip is shown,
 *    and submit POSTs to /api/tasks/bulk creating one task per firm.
 *
 * Editing is single-firm only.
 */
export default function TaskFormDialog({
  open,
  editing,                 // task obj when editing (single mode only)
  users,                   // assignee options
  bulkFirms,               // [{subdomain, firm_name}, ...] for bulk-create
  onClose,
  onSaved,                 // (result) => void
}) {
  const isBulk = Array.isArray(bulkFirms) && bulkFirms.length > 0 && !editing;
  const { refresh: refreshNavCounts } = useNavCounts();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeEmail, setAssigneeEmail] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [firmSubdomain, setFirmSubdomain] = useState("");
  const [firmSearch, setFirmSearch] = useState("");
  const [firmOptions, setFirmOptions] = useState([]);
  const [firmDropdownOpen, setFirmDropdownOpen] = useState(false);
  const [trialFirmName, setTrialFirmName] = useState("");
  const [trialFirmUrl, setTrialFirmUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!isBulk) {
      api.get("/call-logs/firm-options").then(r => setFirmOptions(r.data?.firms || [])).catch(() => {});
    }
    if (editing) {
      setTitle(editing.title || "");
      setDescription(editing.description || "");
      setAssigneeEmail(editing.assignee_email || "");
      setDueDate(editing.due_date || "");
      setIsUrgent(!!editing.is_urgent);
      setFirmSubdomain(editing.firm_subdomain || "");
      setFirmSearch(editing.firm_name || "");
      setTrialFirmName(editing.trial_firm_name || "");
      setTrialFirmUrl(editing.trial_firm_url || "");
    } else {
      setTitle(""); setDescription(""); setAssigneeEmail(""); setDueDate(""); setIsUrgent(false);
      setFirmSubdomain(""); setFirmSearch(""); setTrialFirmName(""); setTrialFirmUrl("");
    }
  }, [open, editing, isBulk]);

  const filteredFirms = useMemo(() => {
    const q = firmSearch.trim().toLowerCase();
    if (!q) return firmOptions.slice(0, 30);
    return firmOptions
      .filter((f) => (f.firm_name || "").toLowerCase().includes(q) || (f.subdomain || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [firmSearch, firmOptions]);

  const valid = title.trim().length > 0 && (editing ? true : !!assigneeEmail);
  const assigneeLabel = useMemo(() => {
    const u = users.find((x) => x.email === assigneeEmail);
    return (u?.name || u?.email || "").split(/\s+/)[0] || "";
  }, [users, assigneeEmail]);

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const common = {
        title: title.trim(),
        description: description.trim(),
        is_urgent: isUrgent,
        due_date: dueDate || null,
      };
      if (editing) {
        await api.patch(`/tasks/${editing.task_id}`, {
          ...common,
          assignee_email: assigneeEmail,
          firm_subdomain: firmSubdomain || "",   // "" clears on edit
          trial_firm_name: trialFirmName.trim() || null,
          trial_firm_url: trialFirmUrl.trim() || null,
        });
        toast.success("Task updated");
        refreshNavCounts();
        onSaved();
      } else if (isBulk) {
        const subs = bulkFirms.map((f) => f.subdomain);
        const res = await api.post("/tasks/bulk", {
          ...common,
          assignee_email: assigneeEmail,
          firm_subdomains: subs,
        });
        const n = res?.data?.created ?? subs.length;
        toast.success(`${n} task${n === 1 ? "" : "s"} assigned${assigneeLabel ? ` to ${assigneeLabel}` : ""}`);
        refreshNavCounts();
        onSaved({ created: n });
      } else {
        await api.post("/tasks", {
          ...common,
          assignee_email: assigneeEmail,
          firm_subdomain: firmSubdomain || "",
          trial_firm_name: trialFirmName.trim() || null,
          trial_firm_url: trialFirmUrl.trim() || null,
        });
        toast.success("Task assigned");
        refreshNavCounts();
        onSaved();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle>
            {editing
              ? "Edit task"
              : isBulk
                ? `New task · ${bulkFirms.length} firm${bulkFirms.length === 1 ? "" : "s"}`
                : "New task"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isBulk
              ? `Create one task for each of the ${bulkFirms.length} selected firms, sharing the same title, description, assignee, due date and urgent flag.`
              : "Create or edit a task for a teammate; you can optionally link it to a single live firm."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isBulk ? "e.g. Reach out and check usage drop" : "e.g. Call R P Khandelwal — they have queries"}
              className="mt-1"
              data-testid="task-title"
            />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any context… (same description applied to all selected firms)"
              className="mt-1"
              data-testid="task-description"
            />
          </div>
          <div>
            <Label>Assignee</Label>
            <Select value={assigneeEmail} onValueChange={setAssigneeEmail}>
              <SelectTrigger className="mt-1" data-testid="task-assignee-select">
                <SelectValue placeholder="Pick a teammate…" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.email} value={u.email}>
                    {u.name || u.email} <span className="text-[10px] text-gray-400 ml-1">{u.role}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isBulk ? (
            <div>
              <Label>Linked firms</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="mt-1 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-violet-200 bg-violet-50 hover:bg-violet-100 transition-colors text-left"
                    data-testid="task-bulk-firms-pill"
                  >
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-violet-800">
                      <Buildings size={14} weight="fill" />
                      {bulkFirms.length} firm{bulkFirms.length === 1 ? "" : "s"} selected
                    </span>
                    <span className="text-[11px] text-violet-600 font-medium">view list</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0 max-h-72 overflow-y-auto" align="start" data-testid="task-bulk-firms-list">
                  <div className="px-3 py-2 border-b border-gray-100 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                    {bulkFirms.length} firms — same task will be created for each
                  </div>
                  <ul className="py-1">
                    {bulkFirms.map((f) => (
                      <li key={f.subdomain} className="px-3 py-1.5 text-sm text-gray-800 truncate" title={`${f.firm_name} · ${f.subdomain}`}>
                        {f.firm_name}
                        <span className="ml-1 text-[10px] text-gray-400 font-mono">{f.subdomain}</span>
                      </li>
                    ))}
                  </ul>
                </PopoverContent>
              </Popover>
              <p className="mt-1 text-[11px] text-gray-500">
                A separate task will be created for each firm. Title, description, assignee, due date and urgent flag are shared.
              </p>
            </div>
          ) : (
            <>
              <div className="relative">
                <Label>Linked live firm (optional)</Label>
                <Input
                  value={firmSearch}
                  onChange={(e) => { setFirmSearch(e.target.value); setFirmSubdomain(""); setFirmDropdownOpen(true); }}
                  onFocus={() => setFirmDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setFirmDropdownOpen(false), 150)}
                  placeholder="Search by firm name…"
                  className="mt-1"
                  data-testid="task-firm-search"
                  disabled={trialFirmName.trim() !== "" || trialFirmUrl.trim() !== ""}
                />
                {firmSubdomain ? (
                  <button
                    type="button"
                    onClick={() => { setFirmSubdomain(""); setFirmSearch(""); }}
                    className="absolute right-2 top-[34px] text-gray-400 hover:text-gray-600"
                    data-testid="task-firm-clear"
                  >
                    <X size={14} />
                  </button>
                ) : null}
                {firmDropdownOpen && firmSearch && filteredFirms.length > 0 ? (
                  <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg" data-testid="task-firm-dropdown">
                    {filteredFirms.map((f) => (
                      <button
                        type="button"
                        key={f.subdomain}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setFirmSubdomain(f.subdomain);
                          setFirmSearch(f.firm_name);
                          setFirmDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 flex items-center justify-between"
                        data-testid={`task-firm-option-${f.subdomain}`}
                      >
                        <span>{f.firm_name}</span>
                        <span className="text-[10px] text-gray-400">{f.subdomain}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div>
                <Label>Linked trial firm (optional)</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Input
                    value={trialFirmName}
                    onChange={(e) => setTrialFirmName(e.target.value)}
                    placeholder="Trial firm name"
                    className="font-mono text-sm"
                    data-testid="task-trial-firm-name"
                    disabled={firmSubdomain !== ""}
                  />
                  <Input
                    value={trialFirmUrl}
                    onChange={(e) => setTrialFirmUrl(e.target.value)}
                    placeholder="trial1327"
                    className="font-mono text-sm"
                    data-testid="task-trial-firm-url"
                    disabled={firmSubdomain !== ""}
                  />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Due date (optional)</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1" data-testid="task-due-date" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={isUrgent} onCheckedChange={setIsUrgent} data-testid="task-urgent-switch" />
                Urgent
              </label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={save}
            disabled={!valid || busy}
            className="bg-violet-600 text-white hover:bg-violet-700"
            data-testid="task-save-btn"
          >
            {editing
              ? "Save"
              : isBulk
                ? `Assign ${bulkFirms.length} task${bulkFirms.length === 1 ? "" : "s"}`
                : "Assign task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
